import { useMemo, useState } from 'react';
import { getCategories, type ToolCategory } from '../data/tools';
import { useToolStationVisible } from '../contexts/ToolStationVisibleContext';
import { ToolCard } from '../components/ToolCard';
import { GoogleAd } from '../components/GoogleAd';
import { Search, Sparkles } from 'lucide-react';

/**
 * 首頁：搜尋列 + 分類篩選 + 工具卡片網格
 */
export function Dashboard() {
  const { tools } = useToolStationVisible();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<ToolCategory | '全部'>('全部');

  const categories = useMemo(() => ['全部', ...getCategories(tools)], [tools]);

  const filtered = useMemo(() => {
    return tools.filter(t => {
      const matchSearch =
        !search.trim() ||
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        t.description.toLowerCase().includes(search.toLowerCase());
      const matchCategory = category === '全部' || t.category === category;
      return matchSearch && matchCategory;
    });
  }, [tools, search, category]);

  const luckyTool = useMemo(() => {
    if (filtered.length === 0) {
      return null;
    }
    const randomIndex = Math.floor(Math.random() * filtered.length);
    return filtered[randomIndex] ?? null;
  }, [filtered]);

  return (
    <div className="space-y-8">
      <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-[0_8px_30px_rgba(15,23,42,0.06)] dark:border-gray-700 dark:bg-gray-800/90">
        <div className="mb-6 text-center sm:text-left">
          <h1 className="text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
            工具首頁
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            以關鍵字快速找到工具，或直接讓系統幫你隨機挑一個。
          </p>
        </div>

        <div className="flex flex-col gap-3 rounded-full border border-gray-200 bg-gray-50/80 p-2 shadow-[0_2px_16px_rgba(15,23,42,0.08)] backdrop-blur-sm dark:border-gray-600 dark:bg-gray-900/60 sm:flex-row sm:items-center">
          <label className="relative flex-1">
            <Search
              className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              aria-hidden="true"
            />
            <input
              type="search"
              placeholder="搜尋工具名稱或描述..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full rounded-full border border-transparent bg-white py-3 pl-11 pr-4 text-sm text-gray-900 outline-none transition focus:border-gray-300 focus:shadow-[0_0_0_4px_rgba(59,130,246,0.12)] dark:bg-gray-800 dark:text-gray-100 dark:focus:border-gray-500"
              aria-label="搜尋工具"
            />
          </label>
          <button
            type="button"
            disabled={!luckyTool}
            onClick={() => {
              if (luckyTool) {
                window.location.href = `/tools/${luckyTool.slug}`;
              }
            }}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-5 py-3 text-sm font-medium text-gray-700 transition hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          >
            <Sparkles className="h-4 w-4 text-violet-500" />
            試試手氣
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {categories.map(cat => (
            <button
              key={cat}
              type="button"
              onClick={() => setCategory(cat as ToolCategory | '全部')}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                category === cat
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden rounded-3xl border border-gray-200 bg-white p-6 shadow-[0_8px_30px_rgba(15,23,42,0.06)] dark:border-gray-700 dark:bg-gray-800/90">
        <div className="relative z-10 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="max-w-xl">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
              Focus Mode
            </p>
            <h2 className="mt-2 text-xl font-semibold text-gray-900 dark:text-gray-100">
              今日工具清單已就緒，挑一個開始吧
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              目前共有 {filtered.length}{' '}
              個符合條件的工具，支援快速搜尋、分類切換與單頁直達。
            </p>
          </div>
          <div className="h-28 w-full max-w-xs rounded-2xl bg-gradient-to-br from-cyan-300/60 via-violet-300/60 to-fuchsia-300/60 blur-2xl dark:from-cyan-400/30 dark:via-violet-400/30 dark:to-fuchsia-400/30" />
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {filtered.map(tool => (
          <ToolCard key={tool.id} tool={tool} />
        ))}
      </div>

      {filtered.length === 0 && (
        <p className="text-gray-500 dark:text-gray-400">沒有符合條件的工具。</p>
      )}

      {/* 列表底部廣告位 */}
      <div className="mt-8">
        <GoogleAd format="auto" style={{ minHeight: '100px' }} />
      </div>
    </div>
  );
}
